import React from "react";
import PropTypes from "prop-types";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { getPrincipalMode } from "../../../../components/guardian/principalHelpers";
import {
  parseCurrencyToNumber,
  safeNumber,
} from "../../shared/formHelpers";
import { getUnderwritingThreshold } from "../../shared/validateStepHelpers";

/**
 * Indemnity e-sign + underwriting authorization blocks (guardianship principal step only).
 */
export default function GuardianshipPrincipalExtraBlocks({
  index: i,
  guardian,
  guardians,
  data,
  updateData,
  globalData,
  errors = {},
}) {
  const mode = getPrincipalMode(guardian);
  const bondHigh =
    safeNumber(parseCurrencyToNumber(globalData.basicInformation?.bondAmount)) >=
    getUnderwritingThreshold(globalData.basicInformation?.bondSubCategory);

  const patchGuardian = (patch) => {
    const ug = [...guardians];
    ug[i] = { ...ug[i], ...patch };
    updateData({ ...data, guardians: ug });
  };

  if (mode === "pg_lookup" && guardian.professional_guardian) {
    return (
      <>
        {!guardian.pg_gia_on_file && (
          <TextField
            select
            sx={{ mt: 2 }}
            label="Will you prefer to e-sign the Indemnity Agreement?"
            value={guardian.is_esignatures_principal || "No"}
            onChange={(e) => patchGuardian({ is_esignatures_principal: e.target.value })}
            fullWidth
            required
            error={!!errors[`guardians.${i}.is_esignatures_principal`]}
            helperText={errors[`guardians.${i}.is_esignatures_principal`]}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </TextField>
        )}
        {bondHigh && !guardian.pg_loa_on_file && (
          <>
            <Box
              sx={{
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                backgroundColor: "#f9fafb",
                padding: "16px",
                margin: "16px 0",
              }}
            >
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Authorization to Obtain Underwriting Information
              </Typography>
              <Typography variant="body2">
                Do you fully understand and agree that by submitting this request,
                you authorize verification of information and obtaining consumer
                reports (including credit reports) as needed for bonding eligibility?
              </Typography>
            </Box>
            <TextField
              select
              label="Authorization to obtain underwriting information?"
              value={guardian.credit_report_principal || ""}
              onChange={(e) => patchGuardian({ credit_report_principal: e.target.value })}
              fullWidth
              required
              error={!!errors[`guardians.${i}.credit_report_principal`]}
              helperText={errors[`guardians.${i}.credit_report_principal`]}
            >
              <MenuItem value="" disabled>
                Select an option
              </MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          </>
        )}
      </>
    );
  }

  if (mode === "attorney_lookup" && guardian.attorney) {
    return (
      <>
        {!guardian.attorney_gia_on_file && (
          <TextField
            select
            sx={{ mt: 2 }}
            label="Will you prefer to e-sign the Indemnity Agreement?"
            value={guardian.is_esignatures_principal || "No"}
            onChange={(e) => patchGuardian({ is_esignatures_principal: e.target.value })}
            fullWidth
            required
            error={!!errors[`guardians.${i}.is_esignatures_principal`]}
            helperText={errors[`guardians.${i}.is_esignatures_principal`]}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </TextField>
        )}
      </>
    );
  }

  if (mode === "manual") {
    return (
      <>
        <TextField
          select
          sx={{ mt: 2 }}
          label="Will you prefer to e-sign the Indemnity Agreement?"
          value={guardian.is_esignatures_principal || "No"}
          onChange={(e) => patchGuardian({ is_esignatures_principal: e.target.value })}
          fullWidth
          required
          error={!!errors[`guardians.${i}.is_esignatures_principal`]}
          helperText={errors[`guardians.${i}.is_esignatures_principal`]}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {bondHigh &&
          !(
            guardian.is_professional_guardian === "Yes" && guardian.pg_loa_on_file
          ) && (
            <>
              <Box
                sx={{
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  backgroundColor: "#f9fafb",
                  padding: "16px",
                  margin: "16px 0",
                }}
              >
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  Authorization to Obtain Underwriting Information
                </Typography>
                <Typography variant="body2">
                  Do you fully understand and agree that by submitting this request,
                  you authorize verification of information and obtaining consumer
                  reports (including credit reports) as needed for bonding eligibility?
                </Typography>
              </Box>
              <TextField
                select
                label="Authorization to obtain underwriting information?"
                value={guardian.credit_report_principal || ""}
                onChange={(e) =>
                  patchGuardian({ credit_report_principal: e.target.value })
                }
                fullWidth
                required
                error={!!errors[`guardians.${i}.credit_report_principal`]}
                helperText={errors[`guardians.${i}.credit_report_principal`]}
              >
                <MenuItem value="" disabled>
                  Select an option
                </MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </>
          )}
      </>
    );
  }

  return null;
}

GuardianshipPrincipalExtraBlocks.propTypes = {
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.shape({ guardians: PropTypes.array }).isRequired,
  updateData: PropTypes.func.isRequired,
  globalData: PropTypes.object.isRequired,
  errors: PropTypes.object,
};

GuardianshipPrincipalExtraBlocks.defaultProps = {
  errors: {},
};
